import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { HttpCacheService } from './http-cache.service';
import { sedeCacheSegment } from '../utils/active-sede.util';
import { AuthService } from './auth.service';

/**
 * Direccion del mensaje. Espejo del enum del backend: 0 = entrante (el cliente
 * nos escribio), 1 = saliente (el restaurante respondio).
 */
export enum WhatsAppMessageDirection {
  Inbound = 0,
  Outbound = 1
}

/** Resumen de una conversacion en la lista del inbox. */
export interface InboxConversation {
  id: string;
  customerPhone: string;
  customerName: string | null;
  lastMessageAt: string;
  unreadCount: number;
  /** true si la ventana de 24h de Meta sigue abierta (se puede responder libre). */
  windowOpen: boolean;
}

/** Un mensaje dentro del hilo de una conversacion. */
export interface InboxMessage {
  direction: WhatsAppMessageDirection;
  body: string;
  createdAt: string;
  status: string | null;
}

export interface ApiResult<T = unknown> {
  data?: T;
  succeeded: boolean;
  errors?: string[];
  message?: string;
}

/** Respuesta del endpoint de hilo: { messages: [...] }. */
interface ConversationThreadData {
  messages: InboxMessage[];
}

/**
 * Bandeja de entrada bidireccional de WhatsApp (#5). El Owner/Manager lee las
 * conversaciones entrantes y responde manualmente dentro de la ventana de 24h.
 *
 * Reglas de caching (regla dura del repo):
 *  - LECTURAS (`getConversations`, `getThread`) van por `HttpCacheService.get$`
 *    con key `entidad|sede|params`.
 *  - MUTACIONES (`reply`, `markRead`) pegan directo y invalidan su entidad.
 *  - El evento SignalR `ConversationUpserted` NO se invalida aqui: se mapea en el
 *    `CacheInvalidationBridge` leyendo el signal `conversationUpserted` (punto unico).
 */
@Injectable({ providedIn: 'root' })
export class WhatsAppInboxService {
  private http = inject(HttpClient);
  private cache = inject(HttpCacheService);
  private auth = inject(AuthService);

  private readonly apiUrl = `${environment.apiUrl}/whatsapp-inbox`;
  private readonly conversationsEntity = 'whatsapp-conversations';
  private readonly threadEntity = 'whatsapp-thread';

  /** Hub dedicado del inbox. Aislado del realtime operativo (POS/cocina) porque
   * es una pantalla de baja frecuencia y rol acotado (Owner/Manager). */
  private hub: signalR.HubConnection | null = null;

  /** Pulso de `ConversationUpserted`. El CacheInvalidationBridge hace effect sobre
   * este signal para invalidar 'whatsapp-conversations' y 'whatsapp-thread'. */
  readonly conversationUpserted = signal<number>(0);

  /** Lista de conversaciones (cacheada por sede). */
  getConversations(): Observable<ApiResult<InboxConversation[]>> {
    const key = `${this.conversationsEntity}|${sedeCacheSegment()}`;
    return this.cache.get$(key, () =>
      this.http.get<ApiResult<InboxConversation[]>>(`${this.apiUrl}/conversations`)
    );
  }

  /** Hilo de mensajes de una conversacion (cacheado por id). */
  getThread(conversationId: string): Observable<ApiResult<ConversationThreadData>> {
    const key = `${this.threadEntity}|${conversationId}`;
    return this.cache.get$(key, () =>
      this.http.get<ApiResult<ConversationThreadData>>(
        `${this.apiUrl}/conversations/${conversationId}/messages`
      )
    );
  }

  /**
   * Responde manualmente a la conversacion. Mutacion: invalida la lista (sube el
   * orden / baja el no leido) y el hilo (entra el mensaje saliente).
   */
  reply(conversationId: string, body: string): Observable<ApiResult> {
    return this.http
      .post<ApiResult>(`${this.apiUrl}/conversations/${conversationId}/reply`, { body })
      .pipe(tap(() => {
        this.cache.invalidate(this.conversationsEntity);
        this.cache.invalidate(this.threadEntity);
      }));
  }

  /** Marca la conversacion como leida. Mutacion: invalida la lista (badge no leido). */
  markRead(conversationId: string): Observable<ApiResult> {
    return this.http
      .post<ApiResult>(`${this.apiUrl}/conversations/${conversationId}/read`, {})
      .pipe(tap(() => this.cache.invalidate(this.conversationsEntity)));
  }

  /**
   * Conecta al hub `/hubs/conversations` y escucha `ConversationUpserted`. Solo
   * pulsa el signal; la invalidacion de cache la centraliza el CacheInvalidationBridge.
   * Idempotente: si ya hay conexion, no abre otra.
   */
  connectHub(): void {
    if (this.hub) return;

    const token = this.auth.getToken();
    if (!token) return;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiUrl.replace(/\/api\/?$/, '')}/hubs/conversations`, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    this.hub.on('ConversationUpserted', () => {
      this.conversationUpserted.update(v => v + 1);
    });

    this.hub.start().catch(err => {
      console.error('Conversations hub error:', err);
      this.hub = null;
    });
  }

  /** Cierra el hub (al salir de la pantalla del inbox). */
  disconnectHub(): void {
    this.hub?.stop();
    this.hub = null;
  }
}
